import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Images, MonitorDown, Map, FolderUp, ArrowRight, MapPin } from "lucide-react";
import { SuiteLayout, Glass, NeonButton } from "../../components/suite/SuiteLayout";
import { CreditsHud } from "../../components/suite/CreditsHud";
import { UsageChart } from "../../components/suite/UsageChart";
import { Plans, usePlan } from "../../lib/plans";
import { useAuth } from "../../context/AuthContext";

const TOOLS = [
  { to: "/batch", icon: FolderUp, label: "Batch Processing", desc: "Rename, tag and add EXIF in bulk", c: "text-cyan-300" },
  { to: "/sessions", icon: Camera, label: "Camera capture", desc: "Shoot straight into named sessions", c: "text-purple-300" },
  { to: "/gallery", icon: Images, label: "Gallery", desc: "Review, export ZIP", c: "text-emerald-300" },
  { to: "/watcher", icon: MonitorDown, label: "Desktop Watcher", desc: "Auto-upload SD cards", c: "text-cyan-300" },
  { to: "/survey", icon: Map, label: "Aerial survey", desc: "Map, acreage, KML", c: "text-purple-300" },
  { to: "/locate", icon: MapPin, label: "Find by place", desc: "Photos inside an area or near an address", c: "text-emerald-300" },
];

export default function SuiteDashboard() {
  const { user } = useAuth();
  const { plan } = usePlan();
  const [batches, setBatches] = useState([]);
  useEffect(() => { Plans.batches().then(setBatches).catch(() => {}); }, []);
  const noPlan = plan && !["trialing", "active"].includes(plan.status);
  return (
    <SuiteLayout credits={plan?.available} title="Dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10" data-testid="suite-dashboard">
        <p className="font-mono text-xs tracking-[0.3em] text-cyan-400">DASHBOARD</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
        <p className="mt-2 text-sm text-slate-400" data-testid="dashboard-plan-line">
          {plan ? (noPlan ? (plan.trial_eligible ? "No active plan. Start a 7-day free trial to unlock processing." : "No active plan. Choose a plan to keep processing.") : `${plan.plan?.name} · ${plan.status === "trialing" ? "Free trial" : "Active"}${plan.cancel_at_period_end ? " · cancels at period end" : ""}`) : "Loading…"}
        </p>
        <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <CreditsHud plan={plan} />
          <Glass className="p-6 flex flex-col justify-between gap-4">
            <p className="text-slate-300 text-sm">{noPlan ? "Pick Hobbyist, Creator or Pro Suite. You get 10 free credits and nothing is charged for 7 days." : "Drop a folder of images, choose a speed and see the exact credit cost before you start."}</p>
            {noPlan ? <Link to="/#pricing"><NeonButton data-testid="dashboard-choose-plan">Choose a plan <ArrowRight size={16} /></NeonButton></Link>
              : <Link to="/batch"><NeonButton data-testid="dashboard-start-batch">Start a batch <ArrowRight size={16} /></NeonButton></Link>}
          </Glass>
        </div>
        <div className="mt-6"><UsageChart /></div>
        <h2 className="mt-12 mb-4 font-display text-xl font-bold text-white">Tools</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TOOLS.map((t) => (
            <Link key={t.to} to={t.to} className="group rounded-2xl border border-white/10 bg-slate-950/60 p-5 hover:border-cyan-400/50 hover:-translate-y-0.5 transition-[transform,border-color]" data-testid={`dashboard-tool-${t.to.slice(1)}`}>
              <t.icon className={t.c} size={22} />
              <p className="mt-3 font-semibold text-white">{t.label}</p>
              <p className="text-xs text-slate-400">{t.desc}</p>
            </Link>
          ))}
        </div>
        <h2 className="mt-12 mb-4 font-display text-xl font-bold text-white">Recent batches</h2>
        <Glass className="divide-y divide-white/5" data-testid="dashboard-batches">
          {batches.length === 0 && <p className="p-5 text-sm text-slate-500">No batches yet.</p>}
          {batches.map((b) => (
            <div key={b.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`dashboard-batch-${b.id}`}>
              <span className="text-white truncate">{b.label}</span>
              <span className="font-mono text-xs text-slate-400">{b.count - b.refunded} credits · {b.speed} · {new Date(b.created_at).toLocaleString()}</span>
            </div>
          ))}
        </Glass>
      </div>
    </SuiteLayout>
  );
}
